import { request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

const SURVEY_PAGE_API_PATH = '/api/v4/bit/web/survey/page'
const SURVEY_DETAILS_API_PATH = '/api/v4/bit/web/survey/details'
const SURVEY_URL_PATH_API_PATH = '/api/v4/bit/web/survey/url-path'
const SURVEY_EXHIBITION_HALL_API_PATH = '/api/v4/bit/web/survey/exhibition-hall'
const SURVEY_TAB_LIST_API_PATH = '/api/v4/bit/web/survey/tab-list'

export type SurveyEvaluateItem = {
  code: string
  name: string
  route: string
  order?: number
  level?: number
  setting?: string
}

export type SurveyListItem = {
  sid: string
  code: string
  title?: string
  cover?: string
  evaluate?: string
  evaluatecolor?: string
  country?: string
  ico?: string
  company?: string
  date?: string
}

export type SurveyRecentTraderLabel = {
  backgroundColor?: string
  content?: string
  fontColor?: string
}

export type SurveyRecentTrader = {
  code: string
  logo?: string
  ico?: string
  flag?: string
  englishName?: string
  chineseName?: string
  company?: string
  annotation?: string
  color?: string
  score?: number | string
  labelsarr?: string[]
  specialtyLabels?: SurveyRecentTraderLabel[]
  alias: string
}

export type SurveyHotItem = {
  urlPath: string
  cover?: string
  title?: string
  [key: string]: unknown
}

export type SurveyPageParams = {
  code: string
  index: number
  size: number
  ver: number
  count: number
}

export type SurveyDetailsParams = {
  urlPath: string
}

export type SurveyUrlPathParams = {
  surveyId: string
}

export type SurveyExhibitionHallParams = {
  urlPath: string
  index: number
  size: number
}

export type SurveyListParams = {
  code: string
  index: number
  size: number
}

export type SurveyTabListParams = {
  ver: number
}

export type SurveyListResponse = {
  total?: number
  items?: SurveyListItem[]
}

export type SurveyTabListResponse = SurveyEvaluateItem[]

type SurveyTabListWrappedResponse = {
  data?: {
    data?: SurveyEvaluateItem[]
  }
}

export type SurveyPageResponse = {
  data?: {
    tabList?: SurveyEvaluateItem[]
    list?: {
      total?: number
      items?: SurveyListItem[]
    }
    recentTrader?: SurveyRecentTrader[]
  }
}

type SurveyPageWrappedResponse = {
  data?: SurveyPageResponse['data']
}

export type SurveyExhibitionHallResponse = {
  data?: {
    tabList?: SurveyEvaluateItem[]
    exhibitionHall?: {
      total?: number
      items?: SurveyListItem[]
    }
    recentTrader?: SurveyRecentTrader[]
  }
}

export type SurveyDetailData = {
  detail?: Record<string, any>
  top?: SurveyHotItem[]
  related?: Record<string, any>
}

export type SurveyDetailsResponse = {
  data?: SurveyDetailData
}

type SurveyDetailsWrappedResponse = {
  data?: SurveyDetailsResponse['data']
}

export type SurveyUrlPathResponse = {
  data?: Record<string, any>
}

type SurveyUrlPathWrappedResponse = {
  data?: SurveyUrlPathResponse['data']
}

type SurveyExhibitionHallWrappedResponse = {
  data?: SurveyExhibitionHallResponse['data']
}

/**
 * 兼容 SSR 原始响应和客户端拦截器解包后的响应结构。
 * @param response survey/page 接口返回值。
 * @returns 页面消费的统一结构。
 */
const normalizeSurveyPageResponse = (
  response: SurveyPageResponse | SurveyPageWrappedResponse | null,
): SurveyPageResponse | null => {
  if (!response) {
    return null
  }
  if (
    typeof response === 'object' &&
    'data' in response &&
    response.data &&
    ('tabList' in response.data || 'list' in response.data || 'recentTrader' in response.data)
  ) {
    return {
      data: {
        tabList: response.data.tabList ?? [],
        list: {
          total: response.data.list?.total ?? 0,
          items: response.data.list?.items ?? [],
        },
        recentTrader: response.data.recentTrader ?? [],
      },
    }
  }

  return null
}

/**
 * 兼容 SSR 原始响应和客户端拦截器解包后的响应结构。
 * @param response survey/exhibition-hall 接口返回值。
 * @returns 展厅分页消费的统一结构。
 */
const normalizeSurveyExhibitionHallResponse = (
  response: SurveyExhibitionHallResponse | SurveyExhibitionHallWrappedResponse | null,
): SurveyExhibitionHallResponse | null => {
  if (!response) {
    return null
  }

  if (
    typeof response === 'object' &&
    'data' in response &&
    response.data &&
    ('tabList' in response.data || 'exhibitionHall' in response.data || 'recentTrader' in response.data)
  ) {
    return {
      data: {
        tabList: response.data.tabList ?? [],
        exhibitionHall: {
          total: response.data.exhibitionHall?.total ?? 0,
          items: response.data.exhibitionHall?.items ?? [],
        },
        recentTrader: response.data.recentTrader ?? [],
      },
    }
  }

  return null
}

/**
 * 获取实勘列表页聚合数据。
 * @param params 接口查询参数。
 * @returns 兼容 SSR 首屏和客户端路由切换的统一页面数据。
 */
export const getSurveyPage = async (params: SurveyPageParams): Promise<SurveyPageResponse | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: SURVEY_PAGE_API_PATH,
    method: 'GET' as const,
    params,
  }
  const response = import.meta.server
    ? await useServerFetch<SurveyPageWrappedResponse>(requestConfig)
    : await request<unknown, SurveyPageResponse>(requestConfig)
  return normalizeSurveyPageResponse(response)
}

/**
 * 获取实勘详情页数据。
 * @param params 接口查询参数，包含 urlPath。
 * @returns 兼容 SSR 首屏和客户端路由切换的详情数据。
 */
export const getSurveyDetails = async (params: SurveyDetailsParams): Promise<SurveyDetailsResponse | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: SURVEY_DETAILS_API_PATH,
    method: 'GET' as const,
    params,
  }
  const response = import.meta.server
    ? await useServerFetch<SurveyDetailsWrappedResponse>(requestConfig)
    : await request<unknown, SurveyDetailsResponse>(requestConfig)
  return response ?? null
}

/**
 * 根据 urlPath 获取实勘详情的 urlPath。
 * @param params 接口查询参数，包含 urlPath
 * @returns 兼容 SSR 首屏和客户端路由切换的 urlPath 数据。
 */
export const getSurveyUrlPath = async (params: SurveyUrlPathParams): Promise<SurveyUrlPathResponse | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: SURVEY_URL_PATH_API_PATH,
    method: 'GET' as const,
    params,
  }
  const response = import.meta.server
    ? await useServerFetch<SurveyUrlPathWrappedResponse>(requestConfig)
    : await request<unknown, SurveyUrlPathResponse>(requestConfig)

  return response ?? null
}

/**
 * 获取实勘展厅分页数据。
 * @param params 接口查询参数，包含 urlPath、index、size。
 * @returns 兼容 SSR 首屏和客户端路由切换的展厅数据。
 */
export const getSurveyExhibitionHall = async (
  params: SurveyExhibitionHallParams,
): Promise<SurveyExhibitionHallResponse | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: SURVEY_EXHIBITION_HALL_API_PATH,
    method: 'GET' as const,
    params,
  }
  const response = import.meta.server
    ? await useServerFetch<SurveyExhibitionHallWrappedResponse>(requestConfig)
    : await request<unknown, SurveyExhibitionHallResponse>(requestConfig)

  return normalizeSurveyExhibitionHallResponse(response)
}

export const getSurveyTabList = async (params: SurveyTabListParams): Promise<SurveyTabListResponse | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: SURVEY_TAB_LIST_API_PATH,
    method: 'GET' as const,
    params,
  }
  const response = import.meta.server
    ? await useServerFetch<SurveyTabListWrappedResponse>(requestConfig)
    : await request<unknown, SurveyTabListResponse>(requestConfig)

  return normalizeSurveyTabListResponse(response)
}

/**
 * 兼容 SSR 原始响应和客户端拦截器解包后的 tabList 数组。
 * @param response survey/tab-list 接口返回值。
 * @returns 页面消费的 tabList 数组。
 */
const normalizeSurveyTabListResponse = (
  response: SurveyTabListResponse | SurveyTabListWrappedResponse | null,
): SurveyTabListResponse | null => {
  if (!response) {
    return null
  }
  if (Array.isArray(response)) {
    return response
  }

  if (typeof response === 'object' && 'data' in response && response.data && 'data' in response.data) {
    return response.data.data ?? []
  }

  return null
}
